import type { UseMutationResult } from '@tanstack/react-query'

/**
 * Wraps a `useMutation` result so every `mutate`/`mutateAsync` call splices a
 * fresh `crypto.randomUUID()` in as `idempotencyKey` before delegating to the
 * underlying mutation.
 *
 * This is the structural form of the idempotency convention that was previously
 * hand-copied into ~37 mutation hooks (issue #108). Generating the key here —
 * inside the override, once per call — is load-bearing: the key lands in the
 * persisted mutation variables, so the SAME key rides through BOTH replay paths
 * (TanStack reload replay + SW BackgroundSync). Splice order is preserved
 * exactly (`{ idempotencyKey, ...vars }`), so a caller-supplied `idempotencyKey`
 * still wins over the generated one — matching the old hand-written blocks.
 *
 * Two forms:
 *   - `withIdempotency(mutation)` — the public `mutate` vars === the mutation's
 *     own vars type.
 *   - `withIdempotency(mutation, normalize)` — accept a wider/looser public vars
 *     type (e.g. `number | Vars`) that `normalize` maps to the mutation's vars,
 *     replacing the per-hook `normalize` + override boilerplate.
 *
 * `TVars` must carry `idempotencyKey?: string` (the generated key's slot). When
 * the public vars type admits `undefined` (e.g. an optional `{ idempotencyKey? }`
 * regenerate call site), the `vars` argument becomes optional — exactly like the
 * `mutate(vars?: …)` signatures it replaces.
 */

// The internal mutation vars must have a slot for the generated key.
type WithIdempotencyKey = { idempotencyKey?: string }

// `vars` is optional iff the public type admits `undefined`; otherwise required.
// Preserves each hook's required-vs-optional `mutate` signature exactly.
type MutateArgs<TPublic, TOpts> = undefined extends TPublic
  ? [vars?: TPublic, opts?: TOpts]
  : [vars: TPublic, opts?: TOpts]

type MutateOpts<TData, TError, TVars, TContext> = Parameters<
  UseMutationResult<TData, TError, TVars, TContext>['mutate']
>[1]

interface IdempotentMutation<TData, TError, TVars, TContext, TPublic> extends Omit<
  UseMutationResult<TData, TError, TVars, TContext>,
  'mutate' | 'mutateAsync'
> {
  mutate: (...args: MutateArgs<TPublic, MutateOpts<TData, TError, TVars, TContext>>) => void
  mutateAsync: (...args: MutateArgs<TPublic, MutateOpts<TData, TError, TVars, TContext>>) => Promise<TData>
}

export function withIdempotency<TData, TError, TVars extends WithIdempotencyKey, TContext>(
  mutation: UseMutationResult<TData, TError, TVars, TContext>,
): IdempotentMutation<TData, TError, TVars, TContext, TVars>
export function withIdempotency<TData, TError, TVars extends WithIdempotencyKey, TContext, TPublic>(
  mutation: UseMutationResult<TData, TError, TVars, TContext>,
  normalize: (vars: TPublic) => TVars,
): IdempotentMutation<TData, TError, TVars, TContext, TPublic>
export function withIdempotency<TData, TError, TVars extends WithIdempotencyKey, TContext, TPublic>(
  mutation: UseMutationResult<TData, TError, TVars, TContext>,
  normalize?: (vars: TPublic) => TVars,
): IdempotentMutation<TData, TError, TVars, TContext, TPublic> {
  type Opts = MutateOpts<TData, TError, TVars, TContext>

  // Generate the key first, then spread the (normalized) public vars on top —
  // identical to the hand-written `{ idempotencyKey: crypto.randomUUID(), ...v }`.
  const toVars = (vars: TPublic): TVars =>
    ({ idempotencyKey: crypto.randomUUID(), ...(normalize ? normalize(vars) : vars) }) as TVars

  // The impl signature widens `vars` to optional to cover the `MutateArgs`
  // optional-arg overload; the `as TPublic` re-narrows it for `toVars`. Safe:
  // `vars` can only actually be `undefined` when the public type admits it, and
  // those call sites pass a `normalize` that handles `undefined` (`vars ?? {}`).
  return {
    ...mutation,
    mutate: (vars?: TPublic, opts?: Opts) => mutation.mutate(toVars(vars as TPublic), opts),
    mutateAsync: (vars?: TPublic, opts?: Opts) => mutation.mutateAsync(toVars(vars as TPublic), opts),
  } as IdempotentMutation<TData, TError, TVars, TContext, TPublic>
}
